import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, FileText, Clock, CheckCircle, Users } from 'lucide-react';
import PageTransition from '../components/PageTransition';
import { SITE_CONFIG } from '../data';
import './ApplicationPage.css';

const STEPS = [
  { icon: <FileText size={20} />, title: "Formu Doldur", desc: "Google Form üzerinden kişisel ve akademik bilgilerini gir." },
  { icon: <Clock size={20} />,    title: "Değerlendirme", desc: "Başvurular kapandıktan sonra 5 iş günü içinde incelenir." },
  { icon: <CheckCircle size={20} />, title: "Kabul E-postası", desc: "Sonuç e-posta ile bildirilir. Kabul alanlar delegeye özel bilgilendirme alır." },
  { icon: <Users size={20} />,    title: "Çalıştay", desc: "Belirlenen tarihte Çamlıca'da buluşuyoruz!" },
];

const DATES = [
  { label: "Başvuru Son Tarihi", value: "17 Nisan 2026" },
  { label: "Sonuçların Açıklanması", value: "22 Nisan 2026" },
  { label: "Etkinlik Tarihi", value: "02 Mayıs 2026, 03 Mayıs 2026" },
];

export default function ApplicationPage() {
  // Sayfa yüklendiğinde otomatik yönlendirme yapmıyoruz,
  // kullanıcı butona basarak geçecek
  return (
    <PageTransition>
      <div className="app-page">

        <section className="page-hero">
          <div className="page-hero-bg" />
          <div className="container page-hero-content">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="section-tag">Başvuru</span>
              <h1>Hemen Başvur</h1>
              <p>Başvurular Google Form aracılığıyla alınmaktadır. Aşağıdaki butona tıklayarak formu açabilirsiniz.</p>
            </motion.div>
          </div>
        </section>

        <section className="app-main">
          <div className="container app-layout">

            {/* ── CTA Card ── */}
            <motion.div
              className="app-cta-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="app-cta-icon">📋</div>
              <h2>Bireysel Geç Başvuru Formu</h2>
              <p>
                Çamlıca Çalıştay'a katılmak için aşağıdaki Google Form bağlantısını kullanın.
                Form, tercih ettiğiniz komiteyi, kişisel bilgilerinizi ve motivasyon sorularını içermektedir.
              </p>
              <div className="app-cta-note">
                <CheckCircle size={15} />
                <span>Ücretli katılım · Tüm İstanbul liseleri · Sınırlı kontenjan</span>
              </div>
              <a
                href={SITE_CONFIG.googleFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="app-form-btn"
              >
                <ExternalLink size={18} />
                Başvuru Formunu Aç
              </a>
              <p className="app-form-note">
                Yeni bir sekmede Google Forms açılacaktır.
              </p>

              <div className="bosluk"></div>

              <div className="app-cta-icon">📋</div>
              <h2>Delegasyon Geç Başvuru Formu</h2>
              <p>
                Çamlıca Çalıştay'a delegasyon başvurusu yapmak için aşağıdaki Google Form bağlantısını kullanın.
              </p>
              <div className="app-cta-note">
                <CheckCircle size={15} />
                <span>Ücretli katılım · Tüm İstanbul liseleri · Sınırlı kontenjan</span>
              </div>
              <a
                href={SITE_CONFIG.googleFormUrl2}
                target="_blank"
                rel="noopener noreferrer"
                className="app-form-btn"
              >
                <ExternalLink size={18} />
                Başvuru Formunu Aç
              </a>
              <p className="app-form-note">
                Yeni bir sekmede Google Forms açılacaktır.
              </p>

              <div className="app-cta-icon">📋</div>
              <h2>Ziyaretçi Başvurusu</h2>
              <p>
                Çamlıca Çalıştay'a delegasyon başvurusu yapmak için aşağıdaki Google Form bağlantısını kullanın.
              </p>
              <div className="app-cta-note">
                <CheckCircle size={15} />
                <span>Ücretsiz katılım · Tüm İstanbul liseleri · Sınırlı kontenjan</span>
              </div>
              <a
                href={SITE_CONFIG.googleFormUrl3}
                target="_blank"
                rel="noopener noreferrer"
                className="app-form-btn"
              >
                <ExternalLink size={18} />
                Başvuru Formunu Aç
              </a>
              <p className="app-form-note">
                Yeni bir sekmede Google Forms açılacaktır.
              </p>



            </motion.div>

            {/* ── Info Column ── */}
            <div className="app-info-col">

              {/* Süreç */}
              <motion.div
                className="app-info-card"
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3>Başvuru Süreci</h3>
                <div className="steps-list">
                  {STEPS.map((step, i) => (
                    <div key={step.title} className="step-item">
                      <div className="step-num-wrap">
                        <div className="step-icon">{step.icon}</div>
                        {i < STEPS.length - 1 && <div className="step-connector" />}
                      </div>
                      <div className="step-content">
                        <h4>{step.title}</h4>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Tarihler */}
              <motion.div
                className="app-info-card"
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <h3>Önemli Tarihler</h3>
                <ul className="dates-list">
                  {DATES.map(d => (
                    <li key={d.label}>
                      <span className="date-label">{d.label}</span>
                      <strong>{d.value}</strong>
                    </li>
                  ))}
                </ul>
              </motion.div>

            </div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
}
